import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useDispatch, useSelector } from 'react-redux';
import { FC, ReactNode, createContext, useContext, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import { EmailData } from 'types';

import checkIsValidEmail from 'utils/checkIsValidEmail';
import successSwal from 'utils/successSwal';
import errorSwal from 'utils/errorSwal';

import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';
import { useGetPartnerOptions } from 'modules/partners/hooks/useGetPartnerOptions';
import { setModal } from 'modules/_shared/redux/modalActions';
import { sendTestEmail } from 'modules/communications/api/sendTestEmail';
import { createCommunication } from 'modules/communications/redux/communicationActions';

import { MAX_PARTNERS_OPTIONS_COUNT } from 'constants/defaultConstants';
import { sendCommunicationModalInitValue } from '../constants/sendCommunicationModalInitValue';

import { SendCommunicationModalContextType } from '../types/SendCommunicationModalContextType';
import { Recipient } from '../types/Recipient';
import { getUniqueRecipientsByEmail } from '../utils/getUniqueRecipientsByEmail';
import { RecipientFilter } from '../types/RecipientFilter';

import { useGetCategoryOptions } from '../hooks/useGetCategoryOptions';

const SendCommunicationModalContext =
  createContext<SendCommunicationModalContextType>(
    sendCommunicationModalInitValue
  );

export const SendCommunicationModalProvider: FC<{
  children: ReactNode;
  defaultRecipient?: Recipient;
  size?: string;
}> = ({ children, defaultRecipient }) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const user = useSelector((state: State) => state.user);

  const { data: partnerOptionsData, actions: partnerOptionsActions } =
    useGetPartnerOptions({
      societyId: actualSociety?._id || '',
      items: MAX_PARTNERS_OPTIONS_COUNT,
    });

  const { data: categoryOptionsData, actions: categoryOptionsActions } =
    useGetCategoryOptions({
      societyId: actualSociety?._id || '',
    });

  const [recipientsCount, setRecipientsCount] = useState<number>(
    defaultRecipient ? 1 : 0
  );
  const [recipientSource, setRecipientSource] = useState<string>('');
  const [recipients, setRecipients] = useState<Recipient[]>(
    defaultRecipient ? [defaultRecipient] : []
  );
  const [selectedRecipientOptions, setSelectedRecipientOptions] = useState<
    CustomAsyncSelectOption[]
  >([]);
  const [recipientFilter, setRecipientFilter] = useState<RecipientFilter>();
  const [subject, setSubject] = useState<string>('');
  const [body, setBody] = useState<string>('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [testEmailRecipient, setTestEmailRecipient] = useState<string>('');
  const [isValidTestEmailRecipient, setIsValidTestEmailRecipient] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeSubject = (newSubject: string) => {
    setSubject(newSubject);
  };

  const handleChangeBody = (newBody: string) => {
    setBody(newBody);
  };

  const handleAddAttachments = (newFiles: File[]) => {
    setAttachments((prevAttachments) => [...prevAttachments, ...newFiles]);
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prevAttachments) =>
      prevAttachments.filter((_, i) => i !== index)
    );
  };

  const handleChangeRecipientSource = (newRecipientSource: string) => {
    setRecipientSource(newRecipientSource);
  };

  const handleChangeCategory = (option: CustomAsyncSelectOption | null) => {
    if (!option) {
      setRecipients([]);
      setRecipientsCount(0);
      categoryOptionsActions.handleChangeCategoryOption(null);
      setSelectedRecipientOptions([]);
      setRecipientFilter(undefined);
      return;
    }
    categoryOptionsActions.handleChangeCategoryOption(option);

    const newRecipient = {
      id: option.value,
      name: option.name ?? '',
      email: '',
      mergeVars: [],
    };

    setRecipients([newRecipient]);
    setSelectedRecipientOptions([]);
    setRecipientsCount(option.partnersCount?.total ?? 0);
    setRecipientFilter({
      type: 'CATEGORY',
      categoryId: option.value,
    });
  };

  const handleAddRecipient = (option: CustomAsyncSelectOption) => {
    const newRecipient: Recipient = {
      id: option.value,
      name: option.name ?? '',
      email: String(option.email),
      mergeVars: [],
    };
    setRecipients((prevRecipients) => [...prevRecipients, newRecipient]);
    setSelectedRecipientOptions((prevOptions) => [...prevOptions, option]);
    setRecipientsCount((prevCount) => prevCount + 1);
  };

  const handleRemoveRecipient = (id: string) => {
    setRecipients((prevRecipients) =>
      prevRecipients.filter((recipient) => recipient.id !== id)
    );
    setSelectedRecipientOptions((prevOptions) =>
      prevOptions.filter((opt) => opt.value !== id)
    );
    setRecipientsCount((prevCount) => prevCount - 1);
  };

  const handleRemoveCategoryRecipient = () => {
    setRecipients([]);
    setRecipientsCount(0);
    categoryOptionsActions.handleChangeCategoryOption(null);
  };

  const handleChangeTestEmailRecipient = (newTestEmailRecipient: string) => {
    setTestEmailRecipient(newTestEmailRecipient);

    const isValidEmail = checkIsValidEmail(newTestEmailRecipient);
    setIsValidTestEmailRecipient(isValidEmail);
  };

  const handleSendTestEmail = async () => {
    try {
      setIsLoading(true);

      const emailData = {
        to: [
          { email: testEmailRecipient.trim(), name: testEmailRecipient.trim() },
        ],
        subject,
        template: 'sttok-general',
        body,
        user: user?._id,
        society: actualSociety['_id'],
        date: new Date(),
        status: 'SENT',
      };

      await sendTestEmail({ emailData, files: attachments });
      successSwal(t('TestEmailSentSuccessfully'));
    } catch (error) {
      errorSwal(t('ErrorSendingTestEmail'));
    } finally {
      setIsLoading(false);
      setTestEmailRecipient('');
      setIsValidTestEmailRecipient(false);
    }
  };

  const handleSendCommunication = async () => {
    try {
      setIsLoading(true);
      const uniqueRecipients = getUniqueRecipientsByEmail(recipients);

      const emailData: Partial<EmailData> = {
        to: recipientFilter ? [] : uniqueRecipients,
        recipientFilter,
        subject,
        template: 'sttok-general',
        body,
        user: user['_id'],
        society: actualSociety['_id'],
        date: new Date(),
        status: 'SENT',
      };

      await store.dispatch(
        createCommunication({ emailData, files: attachments })
      );

      successSwal(t('CommunicationSentSuccessfully'));
    } catch (error) {
      errorSwal(t('ErrorSendingCommunication'));
    } finally {
      setIsLoading(false);
      handleCloseModal();
    }
  };

  return (
    <SendCommunicationModalContext.Provider
      value={{
        data: {
          recipientsCount,
          recipientSource,
          recipients,
          selectedRecipientOptions,
          partnerOptions: partnerOptionsData.partnerOptions,
          selectedPartnerCategoryOption:
            categoryOptionsData.selectedCategoryOption,
          partnerCategoryOptions: categoryOptionsData.categoryOptions,
          subject,
          body,
          attachments,
          testEmailRecipient,
          isValidTestEmailRecipient,
          hasDefaultRecipient: !!defaultRecipient,
          isLoading,
        },
        actions: {
          handleCloseModal,
          handleChangeRecipientSource,
          handleAddRecipient,
          handleRemoveRecipient,
          handleSearchPartnerByTerm:
            partnerOptionsActions.handleSearchPartnerByTerm,
          handleChangeCategory,
          handleRemoveCategoryRecipient,
          handleChangeSubject,
          handleChangeBody,
          handleAddAttachments,
          handleRemoveAttachment,
          handleChangeTestEmailRecipient,
          handleSendTestEmail,
          handleSendCommunication,
        },
      }}
    >
      {children}
    </SendCommunicationModalContext.Provider>
  );
};

export const useSendCommunicationModal = () => {
  const context = useContext(SendCommunicationModalContext);
  if (!context) {
    throw new Error(
      'useSendCommunicationModal must be used within a SendCommunicationModalProvider'
    );
  }
  return context;
};
